// frontend/src/components/grid/StatusChipCells.jsx
// Color-coded status chips for purchase orders (header and lines) and
// promotions: a `…Cell` is the AG Grid cellRenderer, which reserves the width
// of every status its vocabulary names (ChipSlot) so its column can be
// measured; PoStatusChip is the prominent chip for a page header, and
// PoStatusBanner the strip that page shows once the PO has ended.

import { useTranslation } from 'react-i18next';
import { ICON, CalendarBlank, CheckCircle, Lock, Package, PaperPlaneTilt, PencilSimple, XCircle } from '../../icons';
import StatusBanner from '../common/StatusBanner';
import StatusChip from '../common/StatusChip';
import ChipSlot from './ChipSlot';

// Each cellRenderer wraps StatusChip with a domain-specific icon and color.
// A value its config does not name raises: rendered as bare text it would read
// as a real status with the wrong colour, and every vocabulary here is closed
// on the server.

function configFor(configs, value, kind) {
  const config = configs[value];
  if (!config) throw new Error(`${kind}: unknown status "${value}"`);
  return config;
}

function chipFor(configs, value, kind, t, prominent = false) {
  const { Icon, weight, color, key } = configFor(configs, value, kind);
  const icon = <Icon size={prominent ? ICON.sm : ICON.xs} weight={weight} />;
  return <StatusChip label={t(key)} color={color} icon={icon} prominent={prominent} />;
}

// An empty cell (a loading row, a group header) renders nothing.
function VocabularyCell({ configs, value, kind }) {
  const { t } = useTranslation();
  if (!value) return null;
  const shown = chipFor(configs, value, kind, t);
  // One per other label: two values may share one (a promotion's legacy
  // 'expired' reads as 'completed').
  const others = new Map(Object.entries(configs).map(([other, { key }]) => [key, other]));
  others.delete(configs[value].key);
  const alternatives = [...others.values()].map((other) => chipFor(configs, other, kind, t));
  return <ChipSlot shown={shown} alternatives={alternatives} />;
}

// ── PO status (derived; see PurchaseOrderHeader.status, and
// PurchaseOrderLine.line_status, which speaks the same vocabulary) ──
// `cancelled` (the `cancel` viewset action) is terminal and distinct from
// `closed` (the `close` action): a cancelled PO never received anything.
//
// Draft is `warning`, not `info`: Open owns `info`, and Draft is precisely the
// state a PO is NOT open in. Amber reads as "unsent, your move", which is the
// action a draft is waiting for.
const PO_STATUS_CONFIG = {
  draft:               { color: 'warning', key: 'purchaseOrders.status_draft',              Icon: PencilSimple,    weight: 'regular' },
  open:                { color: 'info',    key: 'purchaseOrders.status_open',               Icon: PaperPlaneTilt,  weight: 'regular' },
  partially_received:  { color: 'warning', key: 'purchaseOrders.status_partially_received', Icon: Package,         weight: 'regular' },
  received:            { color: 'success', key: 'purchaseOrders.status_received',           Icon: CheckCircle,     weight: 'fill' },
  closed:              { color: 'neutral', key: 'purchaseOrders.status_closed',             Icon: Lock,            weight: 'regular' },
  cancelled:           { color: 'neutral', key: 'purchaseOrders.status_cancelled',          Icon: XCircle,         weight: 'regular' },
};

export function PoStatusCell({ value }) {
  return <VocabularyCell configs={PO_STATUS_CONFIG} value={value} kind="PoStatusCell" />;
}

export function PoStatusChip({ value }) {
  const { t } = useTranslation();
  return chipFor(PO_STATUS_CONFIG, value, 'PoStatusChip', t, true);
}

// The statuses that end a PO short of a full receipt.
const PO_END_BANNER_KEYS = {
  cancelled: 'purchaseOrderDetail.endBanner.cancelled',
  closed:    'purchaseOrderDetail.endBanner.closed',
};

export function PoStatusBanner({ value }) {
  const { t } = useTranslation();
  const { Icon, weight, color } = configFor(PO_STATUS_CONFIG, value, 'PoStatusBanner');
  const key = PO_END_BANNER_KEYS[value];
  if (!key) return null;
  return <StatusBanner label={t(key)} color={color} icon={<Icon size={ICON.lg} weight={weight} />} />;
}

// The deriver helper (derivePromotionStatus) lives in ./statusDerivers.js so
// this file only exports React components
// (react-refresh/only-export-components — HMR boundary).

// ── Promotion effective status (derived: dates + today + status field) ──
const PROMOTION_STATUS_CONFIG = {
  draft:     { color: 'neutral', key: 'promotions.status_draft',     Icon: PencilSimple,  weight: 'regular' },
  scheduled: { color: 'info',    key: 'promotions.status_scheduled', Icon: CalendarBlank, weight: 'regular' },
  active:    { color: 'success', key: 'promotions.status_active',    Icon: CheckCircle,   weight: 'fill' },
  completed: { color: 'neutral', key: 'promotions.status_completed', Icon: Lock,          weight: 'regular' },
  // Promotion.status no longer offers 'expired', but no migration converted
  // the rows that carry it.
  expired:   { color: 'neutral', key: 'promotions.status_completed', Icon: Lock,          weight: 'regular' },
};

export function PromotionStatusCell({ value }) {
  return <VocabularyCell configs={PROMOTION_STATUS_CONFIG} value={value} kind="PromotionStatusCell" />;
}

